import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#0F3A2E] to-[#0A2A20]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#C9A24D] rounded-2xl mb-6 shadow-2xl">
            <i className="ri-shield-check-line text-4xl text-[#0F3A2E]"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Privacy Policy
          </h1>
          <p className="text-lg text-white/90">
            Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-10 lg:p-12 space-y-10">
            
            {/* Introduction */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Introduction
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                Providence Path ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. Please read this policy carefully to understand our practices regarding your personal information.
              </p>
            </div>

            {/* Information We Collect */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Information We Collect
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-[#1F4D3A] mb-2">Personal Information</h3>
                  <p className="text-base text-gray-700 leading-relaxed mb-2">
                    We may collect personal information that you voluntarily provide to us when you:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                    <li>Fill out contact forms or request information</li>
                    <li>Schedule consultations or discovery calls</li>
                    <li>Subscribe to our newsletter or communications</li>
                    <li>Engage with our services</li>
                  </ul>
                  <p className="text-base text-gray-700 leading-relaxed mt-3">
                    This information may include: name, email address, phone number, company name, business revenue information, and any other details you choose to provide.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-[#1F4D3A] mb-2">Automatically Collected Information</h3>
                  <p className="text-base text-gray-700 leading-relaxed">
                    When you visit our website, we may automatically collect certain information about your device and browsing behavior, including: IP address, browser type, operating system, referring URLs, pages viewed, time spent on pages, and other diagnostic data.
                  </p>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                How We Use Your Information
              </h2>
              <p className="text-base text-gray-700 leading-relaxed mb-3">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>To respond to your inquiries and provide customer support</li>
                <li>To schedule and conduct consultations and discovery calls</li>
                <li>To deliver our business consulting and growth services</li>
                <li>To send you marketing communications (with your consent)</li>
                <li>To improve our website, services, and customer experience</li>
                <li>To analyze usage patterns and optimize our offerings</li>
                <li>To comply with legal obligations and protect our rights</li>
              </ul>
            </div>

            {/* Information Sharing */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Information Sharing and Disclosure
              </h2>
              <p className="text-base text-gray-700 leading-relaxed mb-3">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you (e.g., email service providers, scheduling platforms, analytics tools).</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information if required by law or in response to valid legal requests by public authorities.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
                <li><strong>With Your Consent:</strong> We may share your information for any other purpose with your explicit consent.</li>
              </ul>
            </div>

            {/* Data Security */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Data Security
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            {/* Your Rights */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Your Rights and Choices
              </h2>
              <p className="text-base text-gray-700 leading-relaxed mb-3">
                You have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><strong>Access:</strong> You may request access to the personal information we hold about you.</li>
                <li><strong>Correction:</strong> You may request that we correct any inaccurate or incomplete information.</li>
                <li><strong>Deletion:</strong> You may request that we delete your personal information, subject to certain legal exceptions.</li>
                <li><strong>Opt-Out:</strong> You may opt out of receiving marketing communications by following the unsubscribe instructions in our emails or contacting us directly.</li>
              </ul>
              <p className="text-base text-gray-700 leading-relaxed mt-3">
                To exercise any of these rights, please contact us at <a href="mailto:Providencepath@pm.me" className="text-[#C9A24D] hover:text-[#B8963E] font-semibold cursor-pointer">Providencepath@pm.me</a>.
              </p>
            </div>

            {/* Cookies */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Cookies and Tracking Technologies
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                We may use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device. You can control cookie preferences through your browser settings, but disabling cookies may affect the functionality of our website.
              </p>
            </div>

            {/* Third-Party Links */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Third-Party Links
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </div>

            {/* Children's Privacy */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Children's Privacy
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete that information.
              </p>
            </div>

            {/* Changes to Policy */}
            <div>
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Changes to This Privacy Policy
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website with a new "Last Updated" date. Your continued use of our services after such changes constitutes your acceptance of the updated policy.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-br from-[#0F3A2E]/5 to-[#C9A24D]/5 rounded-xl p-8 border border-[#C9A24D]/20">
              <h2 className="text-3xl font-bold text-[#0F3A2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Contact Us
              </h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-base text-gray-700">
                  <strong className="text-[#0F3A2E]">Email:</strong> <a href="mailto:Providencepath@pm.me" className="text-[#C9A24D] hover:text-[#B8963E] font-semibold cursor-pointer">Providencepath@pm.me</a>
                </p>
                <p className="text-base text-gray-700">
                  <strong className="text-[#0F3A2E]">Phone:</strong> <a href="tel:+19207703836" className="text-[#C9A24D] hover:text-[#B8963E] font-semibold cursor-pointer">(920) 770-3836</a>
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
